import { memo } from 'react';
import { Handle, Position, NodeProps, type Node } from '@xyflow/react';
import { ArrowsPointingOutIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { DefinitionNodeData } from '../../../utils/definitionTransform';
import { BranchNode as BranchNodeType } from '../../../types/definition';
import { ValidationError } from '../../../utils/definitionValidation';

interface ExtendedNodeData extends DefinitionNodeData {
  validationErrors?: ValidationError[];
}

function BranchNodeComponent({ data, selected }: NodeProps<Node<ExtendedNodeData>>) {
  const node = data.originalNode as BranchNodeType;
  const errors = data.validationErrors?.filter(e => e.severity === 'error') || [];
  const warnings = data.validationErrors?.filter(e => e.severity === 'warning') || [];
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;
  const hasOutput = node.output && Object.keys(node.output).length > 0;

  const getBorderColor = () => {
    if (hasErrors) return 'border-red-500';
    if (selected) return 'border-orange-500 ring-2 ring-orange-200 dark:ring-orange-800';
    return 'border-orange-400 dark:border-orange-600';
  };

  const getBranchTypeLabel = () => {
    switch (node.branchType) {
      case 'conditional':
        return 'Conditional';
      case 'parallel':
        return 'Parallel';
      case 'loop':
        return 'Loop';
      default:
        return 'Branch';
    }
  };

  const getBranchTypeIcon = () => {
    switch (node.branchType) {
      case 'loop':
        return '↻';
      case 'parallel':
        return '⫿';
      default:
        return '◇';
    }
  };

  return (
    <div
      className={`
        px-4 py-3 rounded-lg border-2 bg-white dark:bg-gray-800 shadow-sm min-w-[160px]
        ${getBorderColor()}
      `}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3 !h-3 !bg-orange-500 !border-2 !border-white dark:!border-gray-800"
      />

      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-md ${hasErrors ? 'bg-red-100 dark:bg-red-900' : 'bg-orange-100 dark:bg-orange-900'}`}>
          <ArrowsPointingOutIcon className={`w-4 h-4 ${hasErrors ? 'text-red-600 dark:text-red-400' : 'text-orange-600 dark:text-orange-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate flex items-center gap-1">
            {data.label}
            {(hasErrors || hasWarnings) && (
              <ExclamationCircleIcon className={`w-4 h-4 ${hasErrors ? 'text-red-500' : 'text-amber-500'}`} />
            )}
          </div>
          <div className="text-xs text-orange-600 dark:text-orange-400 flex items-center gap-1">
            <span>{getBranchTypeIcon()}</span>
            <span>{getBranchTypeLabel()}</span>
          </div>
        </div>
      </div>

      {node.condition && (
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 font-mono truncate">
          {node.condition}
        </div>
      )}

      {node.branchType === 'loop' && node.until && (
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 font-mono truncate">
          until: {node.until}
        </div>
      )}

      <div className="mt-1.5 flex items-center gap-1 flex-wrap">
        {hasOutput && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300">
            Exit
          </span>
        )}
      </div>

      {hasErrors && (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400 truncate">
          {errors[0].message}
        </div>
      )}

      {/* Output handle - main */}
      <Handle
        type="source"
        position={Position.Right}
        id="main"
        className="!w-3 !h-3 !bg-orange-500 !border-2 !border-white dark:!border-gray-800"
      />

      {/* Additional output handles for branches */}
      {node.branchType === 'conditional' && (
        <>
          <Handle
            type="source"
            position={Position.Bottom}
            id="true"
            style={{ left: '30%' }}
            className="!w-3 !h-3 !bg-green-500 !border-2 !border-white dark:!border-gray-800"
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="false"
            style={{ left: '70%' }}
            className="!w-3 !h-3 !bg-red-500 !border-2 !border-white dark:!border-gray-800"
          />
        </>
      )}
    </div>
  );
}

export const BranchNode = memo(BranchNodeComponent);
